import {
  itemIdOf,
  targetKey,
  type ArtifactSummary,
  type NamedItem,
  type ProcessCatalogue,
  type SelectorRead,
  type WorkflowDefinition,
  type WorkflowDefinitionStep,
} from "../../../api/content/processDefinitions";
import type { Selector } from "../../../api/content/authoring/selectorDecoders";
import { portHandle, SOURCE_OUTPUT } from "./graphPorts";
import {
  SELECTION_KINDS,
  type GraphBand,
  type GraphEdge,
  type GraphNode,
  type SelectionSelector,
  type SourceGraphNode,
  type TermsSelector,
} from "./workflowGraph";

/**
 * The sources a level's steps read, as input nodes above its pipeline (P-14): each
 * artifact a selector names, each item selection (a label list, the competency
 * questions, few-shot examples), each choice of ontology terms, and each other item a
 * selector names (such as one filled by a role). One node per source per level, with one
 * edge per input that reads it. The entry being worked on, earlier results and source
 * scopes are not drawn: the card's settings list them.
 */

/** Where a level's nodes go, as `workflowGraph` builds it. */
export interface SourceLevel {
  readonly prefix: string;
  readonly parentId: string | null;
  readonly depth: number;
  readonly ancestry: ReadonlySet<string>;
}

export interface SourceBuilder {
  readonly catalogue: ProcessCatalogue;
  readonly expanded: ReadonlySet<string>;
  readonly nodes: GraphNode[];
  readonly addEdge: (edge: Omit<GraphEdge, "id">) => void;
  /** Draws a producing Workflow inside its open artifact. */
  readonly addProducerLevel: (producer: WorkflowDefinition, level: SourceLevel) => void;
}

/**
 * The kind of item a source holds (`ItemKind`), one or several: each kind is one class of
 * the DMW process ontology (`item_classes.py`), so it is the source's type there (F61).
 */
export interface SourceItemKind {
  readonly kind: string;
  readonly many: boolean;
}

export function sourceItemKind(node: SourceGraphNode): SourceItemKind {
  switch (node.kind) {
    case "artifact":
      return { kind: "artifact", many: false };
    case "item":
      return { kind: node.item.kind, many: false };
    case "terms":
      return { kind: "ontology_module", many: node.modules.length !== 1 };
    case "selection":
      switch (node.selector.kind) {
        case "label_list":
          return { kind: "label_list", many: false };
        case "competency_questions":
          return { kind: "competency_question", many: true };
        case "few_shot_examples":
          return { kind: "few_shot_example", many: true };
      }
  }
}

/** Starts the IDs of nodes inside an artifact's producing Workflow. */
export const PRODUCER_SEPARATOR = "/";

/** `placements`: the level's placements as they run there, bound abstract steps resolved (`placementsAt`). */
export function addSourceNodes(builder: SourceBuilder, placements: readonly WorkflowDefinitionStep[], level: SourceLevel): void {
  // A source several steps read is one node that knows every read's settings.
  const sources = new Map<string, { readonly source: Source; readonly selectors: Selector[] }>();
  const edges: Omit<GraphEdge, "id">[] = [];
  for (const step of placements) {
    const summary = step.kind === "step" ? builder.catalogue.steps.get(step.step) : undefined;
    for (const read of summary?.selectorReads ?? []) {
      for (const source of sourcesOf(read, builder.catalogue)) {
        const id = level.prefix + source.id;
        const found = sources.get(id) ?? { source, selectors: [] };
        found.selectors.push(read.selector);
        sources.set(id, found);
        edges.push({ kind: "read", source: id, sourcePort: portHandle.output(SOURCE_OUTPUT), target: level.prefix + step.key, targetPort: portHandle.input(read.input), route: "feed" });
      }
    }
  }
  for (const [id, { source, selectors }] of sources) {
    addSourceNode(builder, id, source, selectors, level);
  }
  for (const edge of edges) {
    builder.addEdge(edge);
  }
}

type Source =
  | { readonly kind: "artifact"; readonly id: string; readonly item: string; readonly artifact: ArtifactSummary }
  | { readonly kind: "selection"; readonly id: string; readonly selector: SelectionSelector; readonly item: NamedItem | null }
  | { readonly kind: "terms"; readonly id: string; readonly selector: TermsSelector; readonly modules: readonly string[]; readonly propertiesFrom: string | null }
  | { readonly kind: "item"; readonly id: string; readonly item: NamedItem };

function sourcesOf(read: SelectorRead, catalogue: ProcessCatalogue): Source[] {
  const { selector } = read;
  const keys = read.targets.map(targetKey);
  const nameOf = (key: string) => catalogue.artifacts.get(key)?.name ?? catalogue.namedItems.get(key)?.name ?? null;
  if (isSelection(selector)) {
    const item = keys.map((key) => catalogue.namedItems.get(key)).find((named) => named !== undefined) ?? null;
    return [{ kind: "selection", id: `selection:${selector.kind}:${keys.join(",")}${whichItems(selector)}`, selector, item }];
  }
  if (selector.kind === "ontology_terms") {
    // Its targets are its modules, then the item naming its annotation properties (`selectorTargets`).
    const moduleKeys = keys.slice(0, selector.modules.length);
    const from = selector.annotation_properties_from == null ? undefined : keys[selector.modules.length];
    return [
      {
        kind: "terms",
        // Which terms: their kinds, modules and filter. How they are shown is a read's setting.
        id: `terms:${[...selector.term_kinds].sort().join("+")}:${moduleKeys.join(",")}${selector.filter === "all" ? "" : `:${selector.filter}`}`,
        selector,
        modules: moduleKeys.map((key) => nameOf(key) ?? key),
        propertiesFrom: from === undefined ? null : (nameOf(from) ?? from),
      },
    ];
  }
  if (selector.kind !== "item") {
    return [];
  }
  return keys.flatMap((key): Source[] => {
    const artifact = catalogue.artifacts.get(key);
    if (artifact !== undefined) {
      return [{ kind: "artifact", id: `artifact:${key}`, item: key, artifact }];
    }
    const named = catalogue.namedItems.get(key);
    return named === undefined ? [] : [{ kind: "item", id: `item:${key}`, item: named }];
  });
}

/**
 * What else picks the items, beyond the items named: questions by their tags, examples by
 * their tags or a ranking. How the items are rendered (SPARQL, guidelines, notes) is a
 * read's setting and does not make another source.
 */
function whichItems(selector: SelectionSelector): string {
  if (selector.kind === "competency_questions") {
    return selector.tags.length === 0 ? "" : `:tags=${[...selector.tags].sort().join("+")}`;
  }
  if (selector.kind === "few_shot_examples") {
    const examples = selector.examples;
    return examples.source === "items" ? "" : examples.source === "tags" ? `:tags=${[...examples.tags].sort().join("+")}` : `:ranking=${examples.input}`;
  }
  return "";
}

function isSelection(selector: SelectorRead["selector"]): selector is SelectionSelector {
  return SELECTION_KINDS.some((kind) => kind === selector.kind);
}

/** `selectors`: every read of the source at this level, in step order. */
function addSourceNode(builder: SourceBuilder, id: string, source: Source, selectors: readonly Selector[], level: SourceLevel): void {
  const versionOf = (reference: string | undefined) => (reference === undefined ? null : (builder.catalogue.versions.get(reference) ?? null));
  const common = { id, parentId: level.parentId, band: "input" as GraphBand, depth: level.depth, ports: [] };
  switch (source.kind) {
    case "selection": {
      const reads = selectors.filter((selector): selector is SelectionSelector => selector.kind === source.selector.kind);
      builder.nodes.push({ ...common, kind: "selection", version: versionOf(source.item?.reference), selector: source.selector, reads, item: source.item, count: selectionCount(source, builder.catalogue), expandable: false, expanded: false });
      return;
    }
    case "terms": {
      const reads = selectors.filter((selector): selector is TermsSelector => selector.kind === "ontology_terms");
      builder.nodes.push({ ...common, kind: "terms", version: null, selector: source.selector, reads, modules: source.modules, propertiesFrom: source.propertiesFrom, expandable: false, expanded: false });
      return;
    }
    case "item":
      builder.nodes.push({ ...common, kind: "item", version: versionOf(source.item.reference), item: source.item, expandable: false, expanded: false });
      return;
    case "artifact":
      addArtifactNode(builder, { ...common, version: versionOf(source.artifact.reference) }, source, level);
  }
}

/** A label list's labels, or the questions a selection over the whole content version reads. */
function selectionCount(source: Extract<Source, { kind: "selection" }>, catalogue: ProcessCatalogue): number | null {
  switch (source.selector.kind) {
    case "label_list":
      return source.item?.labelCount ?? null;
    case "competency_questions":
      return source.selector.tags.length === 0 ? (catalogue.itemCounts?.get("competency_question") ?? null) : null;
    case "few_shot_examples":
      return null;
  }
}

function addArtifactNode(builder: SourceBuilder, common: Pick<SourceGraphNode, "id" | "parentId" | "band" | "depth" | "ports" | "version">, source: Extract<Source, { kind: "artifact" }>, level: SourceLevel): void {
  const { artifact } = source;
  const { workflows, declaredProducers } = builder.catalogue;
  const made = artifact.madeBy === null ? null : (workflows.get(artifact.madeBy.workflow) ?? null);
  const declaredReference = artifact.madeBy === null ? declaredProducers.get(itemIdOf(artifact.reference)) : undefined;
  const declared = declaredReference === undefined ? null : (workflows.get(declaredReference) ?? null);
  const producer = made ?? declared;
  const producedBy = made !== null ? "made" : declared !== null ? "declared" : null;
  const expandable = producer !== null && !level.ancestry.has(producer.reference);
  const expanded = expandable && builder.expanded.has(common.id);
  builder.nodes.push({ ...common, kind: "artifact", item: source.item, artifact, producer, producedBy, expandable, expanded });
  if (producer !== null && expanded) {
    builder.addProducerLevel(producer, {
      prefix: common.id + PRODUCER_SEPARATOR,
      parentId: common.id,
      depth: level.depth + 1,
      ancestry: new Set([...level.ancestry, producer.reference]),
    });
  }
}
